import { gql } from "lib/apolloClient";
import {
  assignmentFragment,
  entityEventTrackFragment,
} from "./ClassRoomFragments";
import { assessmentFragment } from "Assessments/modules/AssessmentFragments";
import { ibPypElementRatingFragment } from "modules/CommonFragments";

export const createAssignmentMutation = gql`
  mutation createAssignment(
    $label: String
    $leId: ID
    $resource: AssignmentResourceInput
    $contentType: ASSIGNMENT_CONTENT_TYPE_ENUM
    $isClassDiscussionEnabled: Boolean
    $isStudentSubmissionEnabled: Boolean
    $isTeacherEvaluationEnabled: Boolean
    $isStudentEvaluationEnabled: Boolean
    $isSimilarityCheckEnabled: Boolean
    $isPrivate: Boolean
    $courses: [AssignmentCourseInput!]!
    $folderNames: [String!]
    $importedLeId: ID
    $studentIds: [ID!]
    $shouldDuplicateAcrossCourses: Boolean
    $curriculumProgramId: ID
    $source: String
    $unreadMessageCountFilters: UnreadMessageCountFilters
    $isExcludedFromFinalGradeCalculation: Boolean
    $isOnScreenCalculatorEnabled: Boolean
    $showStudentResponse: SOLUTION_VISIBILITY_ENUM
    $state: ASSIGNMENT_STATE_ENUM
    $isTimedTestEnabled: Boolean
    $isFixedStartTime: Boolean
    $isLockScreenEnabled: Boolean
    $isSafeBrowserEnabled: Boolean
    $timedTestDuration: ID
    $isLockSubmissionEnabled: Boolean
    $isSharedWithClassMaterial: Boolean
    $userArchivedFilters: UserArchivedFilters
    $metadata: JSON
    $isIncludedInCourseFlow: Boolean
    $isSendAssignedNotification: Boolean
    $addToTop: Boolean
  ) {
    platform {
      createAssignmentV2(
        input: {
          label: $label
          leId: $leId
          resource: $resource
          contentType: $contentType
          isClassDiscussionEnabled: $isClassDiscussionEnabled
          isStudentSubmissionEnabled: $isStudentSubmissionEnabled
          isTeacherEvaluationEnabled: $isTeacherEvaluationEnabled
          isStudentEvaluationEnabled: $isStudentEvaluationEnabled
          isSimilarityCheckEnabled: $isSimilarityCheckEnabled
          isPrivate: $isPrivate
          courses: $courses
          folderNames: $folderNames
          importedLeId: $importedLeId
          shouldDuplicateAcrossCourses: $shouldDuplicateAcrossCourses
          curriculumProgramId: $curriculumProgramId
          source: $source
          isExcludedFromFinalGradeCalculation: $isExcludedFromFinalGradeCalculation
          isOnScreenCalculatorEnabled: $isOnScreenCalculatorEnabled
          showStudentResponse: $showStudentResponse
          state: $state
          isTimedTestEnabled: $isTimedTestEnabled
          isFixedStartTime: $isFixedStartTime
          isLockScreenEnabled: $isLockScreenEnabled
          isSafeBrowserEnabled: $isSafeBrowserEnabled
          timedTestDuration: $timedTestDuration
          isLockSubmissionEnabled: $isLockSubmissionEnabled
          isSharedWithClassMaterial: $isSharedWithClassMaterial
          metadata: $metadata
          isIncludedInCourseFlow: $isIncludedInCourseFlow
          isSendAssignedNotification: $isSendAssignedNotification
          addToTop: $addToTop
        }
      ) {
        response {
          ...assignmentItem
        }
        warning
      }
    }
  }
  ${assignmentFragment.assignmentFeed}
`;

export const updateAssignmentMutation = gql`
  mutation updateAssignment(
    $id: ID!
    $label: String
    $resource: UpdateAssignmentResourceInput
    $deadline: String
    $isClassDiscussionEnabled: Boolean
    $isStudentSubmissionEnabled: Boolean
    $isTeacherEvaluationEnabled: Boolean
    $isStudentEvaluationEnabled: Boolean
    $isSimilarityCheckEnabled: Boolean
    $isPrivate: Boolean
    $students: StudentsInput
    $publishedAt: String
    $folders: UpdateAssignmentFolderInput
    $leId: ID
    $isExcludedFromFinalGradeCalculation: Boolean
    $state: ASSIGNMENT_STATE_ENUM
    $isTimedTestEnabled: Boolean
    $isFixedStartTime: Boolean
    $isLockScreenEnabled: Boolean
    $isSafeBrowserEnabled: Boolean
    $timedTestDuration: ID
    $isOnScreenCalculatorEnabled: Boolean
    $showStudentResponse: SOLUTION_VISIBILITY_ENUM
    $courses: UpdateAssignmentCourseInput
    $isLockSubmissionEnabled: Boolean
    $isSharedWithClassMaterial: Boolean
    $metadata: JSON
    $userArchivedFilters: UserArchivedFilters
    $isLinkedAssignments: Boolean! = false
    $isIncludedInCourseFlow: Boolean
    $isSendAssignedNotification: Boolean
    $addToTop: Boolean
  ) {
    platform {
      updateAssignmentV2(
        input: {
          id: $id
          label: $label
          leId: $leId
          resource: $resource
          deadline: $deadline
          isClassDiscussionEnabled: $isClassDiscussionEnabled
          isStudentSubmissionEnabled: $isStudentSubmissionEnabled
          isTeacherEvaluationEnabled: $isTeacherEvaluationEnabled
          isStudentEvaluationEnabled: $isStudentEvaluationEnabled
          isSimilarityCheckEnabled: $isSimilarityCheckEnabled
          isPrivate: $isPrivate
          students: $students
          publishedAt: $publishedAt
          folders: $folders
          isExcludedFromFinalGradeCalculation: $isExcludedFromFinalGradeCalculation
          isOnScreenCalculatorEnabled: $isOnScreenCalculatorEnabled
          showStudentResponse: $showStudentResponse
          state: $state
          isTimedTestEnabled: $isTimedTestEnabled
          isFixedStartTime: $isFixedStartTime
          isLockScreenEnabled: $isLockScreenEnabled
          isSafeBrowserEnabled: $isSafeBrowserEnabled
          timedTestDuration: $timedTestDuration
          courses: $courses
          isLockSubmissionEnabled: $isLockSubmissionEnabled
          isSharedWithClassMaterial: $isSharedWithClassMaterial
          metadata: $metadata
          isIncludedInCourseFlow: $isIncludedInCourseFlow
          isSendAssignedNotification: $isSendAssignedNotification
          addToTop: $addToTop
        }
      ) {
        response {
          ...assignmentItem
        }
        warning
      }
    }
  }
  ${assignmentFragment.assignmentEdit}
`;

export const deleteAssignmentMutation = gql`
  mutation deleteAssignment($id: ID!) {
    platform {
      deleteAssignment(input: { id: $id })
    }
  }
`;

export const updateStateForAssignmentMutation = gql`
  mutation updateStateForAssignment(
    $id: ID!
    $state: ASSIGNMENT_STATE_ENUM!
    $studentIds: [ID!]
    $unreadMessageCountFilters: UnreadMessageCountFilters
    $userArchivedFilters: UserArchivedFilters
  ) {
    platform {
      updateStateForAssignment(input: { id: $id, state: $state }) {
        ...assignmentItem
      }
    }
  }
  ${assignmentFragment.assignmentFeed}
`;

export const editStudentAssignmentSubmissionMutation = gql`
  mutation editStudentAssignmentSubmission(
    $id: ID!
    $response: String
    $status: STUDENT_ASSIGNMENT_SUBMISSION_ENUM
    $statusMessage: CreateMessageInput
    $checkForSimilarity: Boolean
    $isWorksheet: Boolean
    $overrideStatus: STUDENT_ASSIGNMENT_SUBMISSION_ENUM_V2
    $overrideStatusMessage: String
    $isResetOverrideStatus: Boolean
  ) {
    platform {
      editStudentAssignmentSubmission(
        input: {
          id: $id
          response: $response
          status: $status
          statusMessage: $statusMessage
          checkForSimilarity: $checkForSimilarity
          isWorksheet: $isWorksheet
          overrideStatus: $overrideStatus
          overrideStatusMessage: $overrideStatusMessage
          isResetOverrideStatus: $isResetOverrideStatus
        }
      ) {
        id
        status
        statusV2
        submittedAt
        systemStatus
        overrideStatusUpdatedBy {
          id
          type
        }
        overrideStatusUpdatedAt
      }
    }
  }
`;

export const updateElementRemarkMutation = gql`
  mutation updateElementRemarkMutation($input: [IBPYPElementRemarkInput!]) {
    planner {
      createElementRemarks(input: $input) {
        response {
          id
        }
      }
    }
  }
`;

export const editStudentAssignmentMutation = gql`
  mutation editStudentAssignment(
    $id: ID!
    $isEvaluated: Boolean
    $isSharedWithStudent: Boolean
    $isEvaluationApplicable: Boolean
    $lockState: LOCKING_STATE_ENUM
  ) {
    platform {
      editStudentAssignment(
        input: {
          id: $id
          isEvaluated: $isEvaluated
          isSharedWithStudent: $isSharedWithStudent
          isEvaluationApplicable: $isEvaluationApplicable
          lockState: $lockState
        }
      ) {
        id
        isEvaluated
        evaluatedAt
        isSharedWithStudent
        isEvaluationApplicable
        status
        lockingState {
          state
        }
        submission {
          id
          status
          statusV2
          systemStatus
          submittedAt
          overrideStatusUpdatedBy {
            id
            type
          }
          overrideStatusUpdatedAt
        }
      }
    }
  }
`;

export const assignmentBulkActionMutation = gql`
  mutation assignmentBulkAction(
    $id: ID!
    $isEvaluated: Boolean
    $isSharedWithStudent: Boolean
    $isEvaluationApplicable: Boolean
    $isReturnStudentAssignment: Boolean
    $remarkForReturnStudentAssignment: String
    $studentAssignmentIds: [ID!]
    $isMoveToArchived: Boolean
    $lockState: LOCKING_STATE_ENUM
    $overrideStatus: STUDENT_ASSIGNMENT_SUBMISSION_ENUM_V2
    $overrideStatusMessage: String
  ) {
    platform {
      assignmentBulkAction(
        input: {
          id: $id
          isEvaluated: $isEvaluated
          isSharedWithStudent: $isSharedWithStudent
          isEvaluationApplicable: $isEvaluationApplicable
          isReturnStudentAssignment: $isReturnStudentAssignment
          remarkForReturnStudentAssignment: $remarkForReturnStudentAssignment
          studentAssignmentIds: $studentAssignmentIds
          isMoveToArchived: $isMoveToArchived
          lockState: $lockState
          overrideStatus: $overrideStatus
          overrideStatusMessage: $overrideStatusMessage
        }
      ) {
        response {
          id
          assignedStudents {
            id
            isEvaluated
            isSharedWithStudent
            isEvaluationApplicable
            status
            lockingState {
              state
            }
            submission {
              id
              status
              statusV2
              systemStatus
              overrideStatusUpdatedBy {
                id
                type
              }
              overrideStatusUpdatedAt
            }
          }
          state {
            state
          }
        }
        warning
      }
    }
  }
`;

export const sendStudentAssignmentSubmissionFeedbackMutation = gql`
  mutation sendStudentAssignmentSubmissionFeedback(
    $input: StudentAssignementSubmissionFeedbackInput!
  ) {
    platform {
      sendStudentAssignmentSubmissionFeedback(input: $input)
    }
  }
`;

export const updateUserMessageStateMutation = gql`
  mutation updateUserMessageState($input: UserMessageUpdateStateInput!) {
    platform {
      updateUserMessageState(input: $input)
    }
  }
`;

export const sendPdftronCommentAddedMutation = gql`
  mutation sendPdftronCommentAddedNotification($id: ID!, $comment: String!) {
    platform {
      sendPdfTronCommentAddedNotification(input: { id: $id, comment: $comment })
    }
  }
`;

export const createEntityEventTrackingMutation = gql`
  mutation createEntityEventTracking(
    $input: [CreateEntityEventTrackingInput!]!
  ) {
    platform {
      createEntityEventTracking(input: $input) {
        response {
          ...entityEventTrackItem
        }
        warning
      }
    }
  }
  ${entityEventTrackFragment.entityEventTrack}
`;

export const createMicrosoftAnonymousAccessLinkMutation = gql`
  mutation createMicrosoftAnonymousAccessLink(
    $input: CreateMicrosoftAnonymousAccessLinkInput!
  ) {
    platform {
      createMicrosoftAnonymousAccessLink(input: $input) {
        url
      }
    }
  }
`;

export const createAssignmentBulkAlertMessageMutation = gql`
  mutation bulkAlertMessage($input: [createAssignmentBulkAlertMessageInput!]) {
    platform {
      createAssignmentBulkAlertMessages(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const addAssignmentActivityMutation = gql`
  mutation addAssignmentActivity($input: AddAssignmentActivityInput!) {
    platform {
      addAssignmentActivity(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const createAiEvaluationMutation = gql`
  mutation createAiEvaluation($input: CreateAiTutorEvaluationInput!) {
    ai {
      createAiTutorEvaluation(input: $input) {
        isSuccess
        warning
        response
      }
    }
  }
`;

export const retryAssignmentStudentTemplateMutation = gql`
  mutation retryAssignmentStudentTemplate(
    $input: retryAssignmentStudentTemplateInput!
  ) {
    platform {
      retryAssignmentStudentTemplate(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;
export const changeAssessmentTypeMutation = gql`
  mutation changeAssessmentType(
    $input: ChangeAssessmentTypeInput!
    $collaboratorsFilter: CollaboratorFilters
    $libraryBannerFilters: LibraryPremiumBannerFilters
  ) {
    planner {
      changeAssessmentType(input: $input) {
        warning
        data {
          id
          ... on Assessment {
            ...assessmentItem
          }
        }
      }
    }
  }
  ${assessmentFragment.assessmentDetails}
`;

export const deleteAssessmentToolResponseMutation = gql`
  mutation deleteAssessmentToolResponse(
    $input: DeleteAssessmentToolResponseInput!
  ) {
    planner {
      deleteAssessmentToolResponse(input: $input)
    }
  }
`;

export const createAiAssessmentEvaluationMutation = gql`
  mutation createAiAssessmentEvaluation(
    $input: CreateAiAssessmentEvaluationInput!
  ) {
    ai {
      createAiAssessmentEvaluation(input: $input) {
        isSuccess
        warning
        response
      }
    }
  }
`;

export const createIbPypElementRatingsMutation = gql`
  mutation createIBPYPElementRatings(
    $elementRatings: [IBPYPElementRatingInput!]
  ) {
    planner {
      createIBPYPElementRatings(input: $elementRatings) {
        ...ratingItem
      }
    }
  }
  ${ibPypElementRatingFragment.studentElementRating}
`;

export const updateLockedStateMutation = gql`
  mutation updateLockedState($input: LearningCourseEntityLockUpdateInput!) {
    planner {
      bulkUpdateLearningCourseEntityLock(input: $input) {
        isSuccess
      }
    }
  }
`;

export const deleteJobMutation = gql`
  mutation integrationDeleteJob($input: IntegrationDeleteJobInput!) {
    integration {
      integrationDeleteJob(input: $input)
    }
  }
`;

export const keepStudentAssignmentAIEvaluationMutation = gql`
  mutation keepStudentAssignmentAIevaluation(
    $input: KeepStudentAssignmentAIEvaluationInput!
  ) {
    platform {
      keepStudentAssignmentAIevaluation(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const validateAssessmentLinkMutation = gql`
  mutation validateAssessmentLink($input: ValidateAssessmentLinkInput!) {
    planner {
      validateAssessmentLink(input: $input) {
        isSuccess
        warning
        allLinksValid
      }
    }
  }
`;

export const createShortUrlMutation = gql`
  mutation createShortUrl($input: UrlShortCodeInput!) {
    platform {
      createShortUrl(input: $input) {
        shortUrl
      }
    }
  }
`;

// this mutation does not query value, because cache for value is manually updated
export const createIbPypElementRatingsWithoutValueMutation = gql`
  mutation createIbPypElementRatingsWithoutValue(
    $elementRatings: [IBPYPElementRatingInput!]
  ) {
    planner {
      createIBPYPElementRatings(input: $elementRatings) {
        id
        source
        ibPYPElementId
        ibPYPElementType
        isDeleted
        creatorType
        evaluationJustification {
          id
          message
        }
        academicCriteriaSet {
          id
          label
          academicCriteriaValues {
            id
          }
          criteriaType
          subType
          isArchived
        }
        academicCriteriaValue {
          id
          label
          abbreviation
          description
          color
        }
      }
    }
  }
`;

export const bulkUpdateToddleDocMessagesMutation = gql`
  mutation bulkUpdateToddleDocMessages(
    $input: BulkUpdateToddleDocMessagesInput!
  ) {
    platform {
      bulkUpdateToddleDocMessages(input: $input) {
        isSuccess
        warning
        __typename
      }
      __typename
    }
  }
`;
